import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import HudFrame from "@/components/primitives/HudFrame";
import GridBackground from "@/components/primitives/GridBackground";
import { apiCreateCheckoutSession } from "@/lib/api";
import { ACCESS_SKUS, PHASE_PAIRS } from "@/lib/sku";

const LETTERS_AH = ["A", "B", "C", "D", "E", "F", "G", "H"];

const PATHS = [
    {
        id: "letter",
        label: "Single Letter",
        price: "From $55.55",
        body: "Unlock one discipline at a time. Move at your own pace.",
        diagram: ({ active }) => (
            <svg viewBox="0 0 120 60" className="w-full h-12">
                <g fill="none" stroke="currentColor" strokeWidth="1.4">
                    <polygon
                        points="60,8 96,28 96,52 60,52 24,52 24,28"
                        opacity={active ? 0.85 : 0.45}
                    />
                </g>
                <text x="60" y="38" textAnchor="middle" fontFamily="Geist, sans-serif" fontSize="22" fontWeight="600" fill="currentColor" opacity={active ? 0.95 : 0.55}>
                    A
                </text>
            </svg>
        ),
    },
    {
        id: "phase",
        label: "Phase Pair",
        price: "From $111.11",
        body: "Unlock two related letters as a phase. (A+B, C+D, E+F, G+H)",
        diagram: ({ active }) => (
            <svg viewBox="0 0 140 60" className="w-full h-12">
                <g fill="none" stroke="currentColor" strokeWidth="1.4">
                    <polygon points="40,10 64,22 64,46 40,46 16,46 16,22" opacity={active ? 0.9 : 0.55} />
                    <polygon points="100,10 124,22 124,46 100,46 76,46 76,22" opacity={active ? 0.9 : 0.55} />
                    <line x1="64" y1="34" x2="76" y2="34" opacity={active ? 0.85 : 0.5} />
                </g>
                <text x="40" y="40" textAnchor="middle" fontFamily="Geist, sans-serif" fontSize="20" fontWeight="600" fill="currentColor" opacity={active ? 0.95 : 0.6}>
                    A
                </text>
                <text x="100" y="40" textAnchor="middle" fontFamily="Geist, sans-serif" fontSize="20" fontWeight="600" fill="currentColor" opacity={active ? 0.95 : 0.6}>
                    B
                </text>
            </svg>
        ),
    },
    {
        id: "full",
        label: "Full Access",
        price: "From $444.44",
        body: "Complete A–H system, unlocked immediately. Recommended path.",
        recommended: true,
        diagram: ({ active }) => (
            <svg viewBox="0 0 200 60" className="w-full h-12">
                <g fill="none" stroke="currentColor" strokeWidth="1.2">
                    {["A","B","C","D","E","F","G","H"].map((l, i) => {
                        const x = 14 + i * 24;
                        return (
                            <g key={l}>
                                <polygon
                                    points={`${x},14 ${x+10},20 ${x+10},36 ${x},42 ${x-10},36 ${x-10},20`}
                                    opacity={active ? 0.85 : 0.5}
                                />
                                <text x={x} y={32} textAnchor="middle" fontFamily="Geist, sans-serif" fontSize="11" fontWeight="600" fill="currentColor" opacity={active ? 0.95 : 0.6}>
                                    {l}
                                </text>
                            </g>
                        );
                    })}
                </g>
            </svg>
        ),
    },
];

const MANIFESTO = [
    "// We do not call this financing.",
    "// We do not call this installments.",
    "// This is structured access to a disciplined system.",
    "// Build Path Access · Progressive Unlock Access.",
];

function ManifestoLine({ text, index }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-50px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, x: -8 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.18 * index, ease: [0.22, 1, 0.36, 1] }}
            data-testid={`manifesto-line-${index}`}
            className="font-mono-tight text-sm sm:text-base text-tca-cyan/85 tracking-[0.04em]"
        >
            {text}
        </motion.div>
    );
}

export default function BuildPathAccess() {
    const [busy, setBusy] = useState(null);

    const startCheckout = async (sku, extra = {}) => {
        if (busy) return;
        const key = `${sku}-${extra.phase || extra.letter || "x"}`;
        setBusy(key);
        try {
            const { url } = await apiCreateCheckoutSession({ sku, ...extra });
            window.location.href = url;
        } catch (err) {
            setBusy(null);
            toast.error(err?.response?.data?.detail || "Checkout failed");
        }
    };

    const renderCardActions = (p) => {
        if (p.id === "full") {
            return (
                <div className="mt-5">
                    <a
                        href="#access-gates"
                        data-testid="path-full-cta"
                        className="
                            inline-flex items-center justify-center w-full
                            font-mono-tight font-semibold uppercase tracking-[0.18em] text-[11px]
                            py-3 border border-tca-cyan/65 text-tca-cyan
                            hover:text-tca-cyan-hi hover:border-tca-cyan hover:shadow-glow-cyan-xs
                            bg-gradient-to-b from-[rgba(0,219,255,0.10)] to-transparent
                            rounded-full transition-all duration-300
                        "
                    >
                        Choose a tier →
                    </a>
                </div>
            );
        }
        if (p.id === "phase") {
            return (
                <div className="mt-5 grid grid-cols-2 gap-1.5" data-testid="path-phase-grid">
                    {PHASE_PAIRS.map((pp) => {
                        const inFlight = busy === `${ACCESS_SKUS.foundation.PHASE}-${pp.phase}`;
                        return (
                            <button
                                key={pp.phase}
                                type="button"
                                disabled={!!busy}
                                data-testid={`path-phase-${pp.phase}`}
                                onClick={() =>
                                    startCheckout(ACCESS_SKUS.foundation.PHASE, { phase: pp.phase })
                                }
                                className="
                                    inline-flex items-center justify-center gap-1.5
                                    font-mono-tight uppercase tracking-[0.16em] text-[10px]
                                    py-2 border border-tca-cyan/45 text-tca-cyan
                                    hover:text-tca-cyan-hi hover:border-tca-cyan
                                    rounded-full transition-all duration-300
                                    disabled:opacity-60
                                "
                            >
                                {inFlight && <Loader2 size={10} className="animate-spin" />}
                                {pp.phase} · {pp.letters.join("+")}
                            </button>
                        );
                    })}
                </div>
            );
        }
        if (p.id === "letter") {
            return (
                <div className="mt-5 grid grid-cols-4 gap-1.5" data-testid="path-letter-grid">
                    {LETTERS_AH.map((L) => {
                        const inFlight = busy === `${ACCESS_SKUS.foundation.LETTER}-${L}`;
                        return (
                            <button
                                key={L}
                                type="button"
                                disabled={!!busy}
                                data-testid={`path-letter-${L}`}
                                onClick={() =>
                                    startCheckout(ACCESS_SKUS.foundation.LETTER, { letter: L })
                                }
                                className="
                                    inline-flex items-center justify-center
                                    font-mono-tight uppercase tracking-[0.10em] text-xs font-semibold
                                    py-2 border border-tca-cyan/45 text-tca-cyan
                                    hover:text-tca-cyan-hi hover:border-tca-cyan
                                    rounded transition-all duration-200
                                    disabled:opacity-60
                                "
                            >
                                {inFlight ? <Loader2 size={11} className="animate-spin" /> : L}
                            </button>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <section
            id="build-path"
            data-testid="build-path-section"
            className="relative bg-tca-deep overflow-hidden"
        >
            <GridBackground fine />

            <div className="relative z-10 max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 py-28 sm:py-36">
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.6 }}
                        className="font-mono-tight text-[10px] sm:text-[11px] uppercase tracking-[0.36em] text-tca-cyan/85 mb-5"
                        data-testid="buildpath-eyebrow"
                    >
                        // Progressive Unlock
                    </motion.div>
                    <motion.h2
                        initial={{ opacity: 0, y: 16, filter: "blur(6px)" }}
                        whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="buildpath-headline"
                        className="font-display text-[42px] leading-[1.04] sm:text-[60px] sm:leading-[1.0] lg:text-[76px] lg:leading-[0.98] tracking-[-0.025em] text-tca-ink"
                    >
                        Build Path Access.
                    </motion.h2>
                    <motion.p
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="font-display italic neon-cyan text-lg sm:text-xl mt-3"
                    >
                        Unlock the system on your terms. No waiting. No gatekeeping.
                    </motion.p>
                </div>

                {/* 3-card row */}
                <div
                    data-testid="path-cards"
                    className="mt-16 sm:mt-20 grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6"
                >
                    {PATHS.map((p, i) => (
                        <motion.div
                            key={p.id}
                            initial={{ opacity: 0, y: 16 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
                            data-testid={`path-card-${p.id}`}
                        >
                            <HudFrame
                                bracketColor="var(--tca-cyan-core)"
                                bracketSize={12}
                                borderTint={p.recommended ? "rgba(0, 224, 232, 0.22)" : "rgba(0, 224, 232, 0.10)"}
                                className="h-full bg-tca-bg-panel/55 backdrop-blur-md hover:bg-tca-bg-panel/80 transition-all duration-500 group"
                                innerClassName="p-6 sm:p-7 flex flex-col h-full min-h-[280px]"
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <span className="font-mono-tight text-[10px] uppercase tracking-[0.28em] text-tca-cyan/80">
                                        {`0${i + 1}`}
                                    </span>
                                    {p.recommended && (
                                        <span className="font-mono-tight text-[9px] uppercase tracking-[0.28em] text-tca-cyan bg-tca-cyan/12 border border-tca-cyan/40 px-2 py-0.5">
                                            RECOMMENDED
                                        </span>
                                    )}
                                </div>
                                <div className="text-tca-cyan/85 group-hover:text-tca-cyan transition-colors mb-3">
                                    {p.diagram({ active: true })}
                                </div>
                                <div className="font-display text-2xl sm:text-3xl text-tca-ink mt-4">
                                    {p.label}
                                </div>
                                <div className="font-mono-tight text-tca-cyan text-base sm:text-lg mt-1">
                                    {p.price}
                                </div>
                                <p className="text-sm text-tca-ink-muted mt-4 leading-snug flex-1">
                                    {p.body}
                                </p>
                                {renderCardActions(p)}
                            </HudFrame>
                        </motion.div>
                    ))}
                </div>

                {/* Manifesto block */}
                <div
                    data-testid="manifesto-block"
                    className="mt-20 sm:mt-24 max-w-2xl mx-auto pl-6 border-l border-tca-cyan/40 space-y-2.5"
                >
                    {MANIFESTO.map((line, i) => (
                        <ManifestoLine key={i} text={line} index={i} />
                    ))}
                </div>
            </div>
        </section>
    );
}
